/**
 * IDF Language Service
 *
 * Provides IntelliSense features for the IDF editor using the IDD schema:
 * - Auto-completion for object class names
 * - Auto-completion for field values (choices, references)
 * - Hover documentation from IDD memos
 */

import type * as Monaco from 'monaco-editor';
import type { IDDSchema, IDDObjectType, IDDField } from '../idd/types';
import { IDF_LANGUAGE_ID } from './idf-language';

/**
 * Register completion provider for IDF files
 */
export function registerCompletionProvider(
  monaco: typeof Monaco,
  getSchema: () => IDDSchema | null
): Monaco.IDisposable {
  return monaco.languages.registerCompletionItemProvider(IDF_LANGUAGE_ID, {
    triggerCharacters: [',', ';', '\n', ' '],

    provideCompletionItems(model, position): Monaco.languages.CompletionList {
      const schema = getSchema();
      if (!schema) {
        return { suggestions: [] };
      }

      const lineContent = model.getLineContent(position.lineNumber);
      const textUntilPosition = lineContent.substring(0, position.column - 1);

      // Determine what kind of completion we need
      const context = getCompletionContext(model, position, textUntilPosition);

      switch (context.type) {
        case 'className':
          return getClassNameCompletions(monaco, schema, context.word, position);
        case 'fieldValue':
          return getFieldValueCompletions(
            monaco,
            schema,
            context.className,
            context.fieldIndex,
            context.word,
            position,
            model
          );
        default:
          return { suggestions: [] };
      }
    },
  });
}

/**
 * Register hover provider for IDF files
 */
export function registerHoverProvider(
  monaco: typeof Monaco,
  getSchema: () => IDDSchema | null
): Monaco.IDisposable {
  return monaco.languages.registerHoverProvider(IDF_LANGUAGE_ID, {
    provideHover(model, position): Monaco.languages.Hover | null {
      const schema = getSchema();
      if (!schema) {
        return null;
      }

      // Find the current object and field context
      const context = getHoverContext(model, position);
      if (!context) {
        return null;
      }

      const objectType = schema.objectTypes.get(context.className.toLowerCase());
      if (!objectType) {
        return null;
      }

      // Hovering over class name
      if (context.isClassName) {
        return createObjectHover(objectType, position);
      }

      // Hovering over a field value
      if (context.fieldIndex !== undefined && context.fieldIndex < objectType.fields.length) {
        const field = objectType.fields[context.fieldIndex];
        if (field) {
          return createFieldHover(field, objectType);
        }
      }

      return null;
    },
  });
}

/**
 * Completion context types
 */
type CompletionContext =
  | { type: 'className'; word: string }
  | { type: 'fieldValue'; className: string; fieldIndex: number; word: string }
  | { type: 'none' };

/**
 * Determine what kind of completion is needed at the current position
 */
function getCompletionContext(
  model: Monaco.editor.ITextModel,
  position: Monaco.Position,
  textUntilPosition: string
): CompletionContext {
  // Check if we're at the start of a line or after a semicolon (class name position)
  const trimmedLine = textUntilPosition.trim();

  // If line is empty or we're at the very start, suggest class names
  if (trimmedLine === '' || /^[A-Za-z][A-Za-z0-9:_-]*$/.test(trimmedLine)) {
    // Check if this looks like the start of an object (not in the middle of one)
    if (isStartOfObject(model, position)) {
      return { type: 'className', word: trimmedLine };
    }
  }

  // We're likely in a field value position
  const objectContext = findCurrentObject(model, position);
  if (objectContext) {
    // Count commas to determine field index
    const fieldIndex = countFieldsSoFar(model, objectContext.startLine, position);
    const currentWord = extractCurrentWord(textUntilPosition);
    return {
      type: 'fieldValue',
      className: objectContext.className,
      fieldIndex,
      word: currentWord,
    };
  }

  return { type: 'none' };
}

/**
 * Check if we're at the start of a new object definition
 */
function isStartOfObject(model: Monaco.editor.ITextModel, position: Monaco.Position): boolean {
  // Look backwards for a semicolon (which ends the previous object)
  for (let line = position.lineNumber; line >= 1; line--) {
    const lineContent = model.getLineContent(line);
    const endCol = line === position.lineNumber ? position.column - 1 : lineContent.length;
    const text = lineContent.substring(0, endCol);

    // Skip empty lines and comments
    const trimmed = text.replace(/!.*$/, '').trim();
    if (trimmed === '') continue;

    // If we find a semicolon, we're at the start of a new object
    if (trimmed.endsWith(';')) {
      return true;
    }

    // If we find a comma, we're in the middle of an object
    if (trimmed.includes(',')) {
      return false;
    }

    // If we find a class name pattern at line start, check if it has comma
    if (/^[A-Za-z][A-Za-z0-9:_-]*\s*,/.test(lineContent)) {
      return false;
    }
  }

  // At the start of the file
  return true;
}

/**
 * Find the current object context (class name and start line)
 */
function findCurrentObject(
  model: Monaco.editor.ITextModel,
  position: Monaco.Position
): { className: string; startLine: number } | null {
  // Search backwards for the class name
  for (let line = position.lineNumber; line >= 1; line--) {
    const lineContent = model.getLineContent(line);

    // Look for class name pattern: "ClassName,"
    const match = lineContent.match(/^([A-Za-z][A-Za-z0-9:_-]*)\s*,/);
    if (match && match[1]) {
      return { className: match[1], startLine: line };
    }

    // If we hit a semicolon, we've gone past our object
    if (lineContent.includes(';') && line < position.lineNumber) {
      break;
    }
  }

  return null;
}

/**
 * Count the number of fields (commas) from object start to current position
 */
function countFieldsSoFar(
  model: Monaco.editor.ITextModel,
  startLine: number,
  position: Monaco.Position
): number {
  let fieldCount = 0;

  for (let line = startLine; line <= position.lineNumber; line++) {
    const lineContent = model.getLineContent(line);
    const endCol = line === position.lineNumber ? position.column - 1 : lineContent.length;
    const text = lineContent.substring(0, endCol);

    // Remove comments
    const withoutComments = text.replace(/!.*$/, '');

    // Count commas (each comma ends a field)
    const commas = (withoutComments.match(/,/g) || []).length;
    fieldCount += commas;

    // The first comma after class name is field 0, second is field 1, etc.
    // So we subtract 1 for the comma after the class name on the start line
    if (line === startLine && commas > 0) {
      fieldCount--; // The first comma is after the class name, not a field
    }
  }

  return fieldCount;
}

/**
 * Extract the current word being typed
 */
function extractCurrentWord(text: string): string {
  // Get the word after the last delimiter (comma, space, etc.)
  const match = text.match(/[,\s]([^,\s]*)$/);
  if (match && match[1]) {
    return match[1];
  }
  return text.trim();
}

/**
 * Get completions for class names
 */
function getClassNameCompletions(
  monaco: typeof Monaco,
  schema: IDDSchema,
  word: string,
  position: Monaco.Position
): Monaco.languages.CompletionList {
  const suggestions: Monaco.languages.CompletionItem[] = [];
  const wordLower = word.toLowerCase();

  for (const [, objectType] of schema.objectTypes) {
    if (objectType.isObsolete) continue;

    if (objectType.name.toLowerCase().startsWith(wordLower)) {
      suggestions.push({
        label: objectType.name,
        kind: monaco.languages.CompletionItemKind.Class,
        detail: objectType.group || '',
        documentation: objectType.memo || '',
        insertText: objectType.name + ',',
        range: {
          startLineNumber: position.lineNumber,
          startColumn: position.column - word.length,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        },
      });
    }
  }

  // Sort by name
  suggestions.sort((a, b) => (a.label as string).localeCompare(b.label as string));

  return { suggestions };
}

/**
 * Get completions for field values
 */
function getFieldValueCompletions(
  monaco: typeof Monaco,
  schema: IDDSchema,
  className: string,
  fieldIndex: number,
  word: string,
  position: Monaco.Position,
  model: Monaco.editor.ITextModel
): Monaco.languages.CompletionList {
  const suggestions: Monaco.languages.CompletionItem[] = [];
  const objectType = schema.objectTypes.get(className.toLowerCase());

  if (!objectType || fieldIndex < 0 || fieldIndex >= objectType.fields.length) {
    return { suggestions };
  }

  const field = objectType.fields[fieldIndex];
  if (!field) {
    return { suggestions };
  }

  const wordLower = word.toLowerCase();

  // Add choice values
  if (field.choices) {
    for (const choice of field.choices) {
      if (choice.toLowerCase().startsWith(wordLower)) {
        suggestions.push({
          label: choice,
          kind: monaco.languages.CompletionItemKind.EnumMember,
          detail: `Choice for ${field.name || field.id}`,
          insertText: choice,
          range: createRange(position, word.length),
        });
      }
    }
  }

  // Add autosize/autocalculate if applicable
  if (field.autosizable && 'autosize'.startsWith(wordLower)) {
    suggestions.push({
      label: 'autosize',
      kind: monaco.languages.CompletionItemKind.Keyword,
      detail: 'Automatically size this field',
      insertText: 'autosize',
      range: createRange(position, word.length),
    });
  }

  if (field.autocalculatable && 'autocalculate'.startsWith(wordLower)) {
    suggestions.push({
      label: 'autocalculate',
      kind: monaco.languages.CompletionItemKind.Keyword,
      detail: 'Automatically calculate this field',
      insertText: 'autocalculate',
      range: createRange(position, word.length),
    });
  }

  // Add object-list references
  if (field.objectList) {
    const referencedObjects = getReferencedObjectNames(model, schema, field.objectList);
    for (const name of referencedObjects) {
      if (name.toLowerCase().startsWith(wordLower)) {
        suggestions.push({
          label: name,
          kind: monaco.languages.CompletionItemKind.Reference,
          detail: `Reference (${field.objectList})`,
          insertText: name,
          range: createRange(position, word.length),
        });
      }
    }
  }

  // Add default value if available
  if (field.default && field.default.toLowerCase().startsWith(wordLower)) {
    suggestions.push({
      label: field.default,
      kind: monaco.languages.CompletionItemKind.Value,
      detail: 'Default value',
      insertText: field.default,
      range: createRange(position, word.length),
    });
  }

  return { suggestions };
}

/**
 * Get names of objects that can be referenced by an object-list
 */
function getReferencedObjectNames(
  model: Monaco.editor.ITextModel,
  schema: IDDSchema,
  objectListName: string
): string[] {
  const names: string[] = [];
  const refTypes = schema.referenceLists.get(objectListName);

  if (!refTypes) {
    return names;
  }

  // Scan the document for objects of the referenced types
  const content = model.getValue();
  const lines = content.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const currentLine = lines[i];
    if (!currentLine) continue;

    // Look for class name pattern
    const classMatch = currentLine.match(/^([A-Za-z][A-Za-z0-9:_-]*)\s*,/);
    if (classMatch && classMatch[1]) {
      const className = classMatch[1];
      if (refTypes.has(className)) {
        // Get the name field (usually first field after class name)
        // Look at the same line after comma or next lines
        const nameMatch = currentLine.match(/,\s*([^,;!]+)/);
        if (nameMatch && nameMatch[1]) {
          const name = nameMatch[1].trim();
          if (name && !names.includes(name)) {
            names.push(name);
          }
        } else {
          // Check next line
          const nextLine = lines[i + 1];
          if (nextLine) {
            const nextMatch = nextLine.match(/^\s*([^,;!]+)/);
            if (nextMatch && nextMatch[1]) {
              const name = nextMatch[1].trim();
              if (name && !names.includes(name)) {
                names.push(name);
              }
            }
          }
        }
      }
    }
  }

  return names;
}

/**
 * Create a Monaco range for completion
 */
function createRange(
  position: Monaco.Position,
  wordLength: number
): Monaco.IRange {
  return {
    startLineNumber: position.lineNumber,
    startColumn: position.column - wordLength,
    endLineNumber: position.lineNumber,
    endColumn: position.column,
  };
}

/**
 * Hover context for documentation
 */
interface HoverContext {
  className: string;
  isClassName: boolean;
  fieldIndex?: number;
}

/**
 * Get context for hover documentation
 */
function getHoverContext(
  model: Monaco.editor.ITextModel,
  position: Monaco.Position
): HoverContext | null {
  const lineContent = model.getLineContent(position.lineNumber);

  // Check if we're hovering over a class name
  const classMatch = lineContent.match(/^([A-Za-z][A-Za-z0-9:_-]*)\s*,/);
  if (classMatch && classMatch[1]) {
    const classNameEnd = classMatch[1].length;
    if (position.column <= classNameEnd + 1) {
      return { className: classMatch[1], isClassName: true };
    }
  }

  // Find the current object
  const objectContext = findCurrentObject(model, position);
  if (objectContext) {
    const fieldIndex = countFieldsSoFar(model, objectContext.startLine, position);
    return {
      className: objectContext.className,
      isClassName: false,
      fieldIndex,
    };
  }

  return null;
}

/**
 * Create hover content for an object type
 */
function createObjectHover(
  objectType: IDDObjectType,
  position: Monaco.Position
): Monaco.languages.Hover {
  const contents: Monaco.IMarkdownString[] = [];

  // Title
  contents.push({
    value: `**${objectType.name}**`,
  });

  // Group
  if (objectType.group) {
    contents.push({
      value: `*Group: ${objectType.group}*`,
    });
  }

  // Memo
  if (objectType.memo) {
    contents.push({
      value: objectType.memo,
    });
  }

  // Properties
  const props: string[] = [];
  if (objectType.isUnique) props.push('unique-object');
  if (objectType.isRequired) props.push('required-object');
  if (objectType.minFields > 0) props.push(`min-fields: ${String(objectType.minFields)}`);
  if (objectType.extensible > 0) props.push(`extensible: ${String(objectType.extensible)}`);

  if (props.length > 0) {
    contents.push({
      value: `\`${props.join(' | ')}\``,
    });
  }

  return {
    contents,
    range: {
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: objectType.name.length + 1,
    },
  };
}

/**
 * Create hover content for a field
 */
function createFieldHover(
  field: IDDField,
  objectType: IDDObjectType
): Monaco.languages.Hover {
  const contents: Monaco.IMarkdownString[] = [];

  // Title
  contents.push({
    value: `**${field.name || field.id}** (${objectType.name})`,
  });

  // Type and units
  let typeInfo = `Type: \`${field.type}\``;
  if (field.units) {
    typeInfo += ` | Units: \`${field.units}\``;
  }
  contents.push({ value: typeInfo });

  // Memo
  if (field.memo) {
    contents.push({
      value: field.memo,
    });
  }

  // Range constraints
  if (field.minimum !== undefined || field.maximum !== undefined) {
    let range = 'Range: ';
    if (field.minimum !== undefined) {
      range += field.exclusiveMinimum ? `> ${String(field.minimum)}` : `>= ${String(field.minimum)}`;
    }
    if (field.minimum !== undefined && field.maximum !== undefined) {
      range += ' and ';
    }
    if (field.maximum !== undefined) {
      range += field.exclusiveMaximum ? `< ${String(field.maximum)}` : `<= ${String(field.maximum)}`;
    }
    contents.push({ value: range });
  }

  // Default value
  if (field.default) {
    contents.push({
      value: `Default: \`${field.default}\``,
    });
  }

  // Choices
  if (field.choices && field.choices.length > 0) {
    contents.push({
      value: `Choices: ${field.choices.map((c) => `\`${c}\``).join(', ')}`,
    });
  }

  // Properties
  const props: string[] = [];
  if (field.required) props.push('required');
  if (field.autosizable) props.push('autosizable');
  if (field.autocalculatable) props.push('autocalculatable');

  if (props.length > 0) {
    contents.push({
      value: `\`${props.join(' | ')}\``,
    });
  }

  return {
    contents,
  };
}

/**
 * Dispose of all registered providers
 */
export interface LanguageServiceDisposables {
  completion: Monaco.IDisposable;
  hover: Monaco.IDisposable;
}

/**
 * Register all IDF language service providers
 */
export function registerIDFLanguageService(
  monaco: typeof Monaco,
  getSchema: () => IDDSchema | null
): LanguageServiceDisposables {
  return {
    completion: registerCompletionProvider(monaco, getSchema),
    hover: registerHoverProvider(monaco, getSchema),
  };
}
